import React, { useState, useEffect } from "react";
import { httpService } from "../../services/httpService";
import { useParams } from "react-router-dom";
import DynamicTable from "../common/DynamicTable";

const SupplierProduct = () => {
  const { supplierId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supplierId) {
      setError("No supplierId provided.");
      setLoading(false);
      return;
    }

    const fetchProductData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await httpService.get(`/vendorsupplier/getVendorSupplierProducts/${supplierId}`);
        
        // Check the response structure
        if (response.status && response.code === 200) {
          // The products are inside response.data.products
          const productsData = response.data?.products || [];
          setProducts(productsData);
        } else if (response.code === 200) {
          // Handle case where status might not be present
          const productsData = response.data?.products || [];
          setProducts(productsData);
        } else {
          console.error("API Error:", response);
          setError(response.message || "Failed to fetch product data");
        }
      } catch (err) {
        console.error("Fetch Error:", err);
        console.error("Error details:", err.response);
        setError(err.response?.data?.message || err.message || "An error occurred while fetching product data");
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [supplierId]);

  const columns = [
    { key: "product_name", header: "NAME", sortable: true, renderCell: row => row.product_name || "-" },
    {
      key: "product_category",
      header: "CATEGORY",
      sortable: true,
      renderCell: (row) =>
        Array.isArray(row.product_category) && row.product_category.length > 0
          ? row.product_category.map((value) => value.name).join(", ")
          : "-",
      sortValue: (row) => {
        if (Array.isArray(row.product_category) && row.product_category.length > 0) {
          return row.product_category.map((value) => value.name).join(", ");
        }
        return "";
      }
    },
    { key: "hsn_code", header: "HSN CODE", sortable: true, renderCell: row => row.hsn_code || "-" },
    { key: "sku_number", header: "SKU #", sortable: true, renderCell: row => row.sku_number || "-" },
    { 
      key: "available_qty", 
      header: "QUANTITY", 
      sortable: true, 
      renderCell: row => row.available_qty ?? "-",
      sortValue: (row) => row.available_qty ?? 0
    },
    { 
      key: "purchase_price", 
      header: "PURCHASE PRICE", 
      sortable: true, 
      renderCell: row => row.purchase_price ?? "-",
      sortValue: (row) => row.purchase_price ?? 0
    },
    { 
      key: "sale_price", 
      header: "SALE PRICE", 
      sortable: true, 
      renderCell: row => row.sale_price ?? "-",
      sortValue: (row) => row.sale_price ?? 0
    },
    { 
      key: "mrp_price", 
      header: "MRP", 
      sortable: true, 
      renderCell: row => row.mrp_price ?? "-",
      sortValue: (row) => row.mrp_price ?? 0
    },
    { 
      key: "gst_slab", 
      header: "GST", 
      sortable: true, 
      renderCell: row => row.gst_slab ?? "-",
      sortValue: (row) => row.gst_slab ?? ""
    },
  ];

  if (loading) {
    return <div className="p-6 text-gray-500 bg-white rounded shadow">Loading products...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 bg-white rounded shadow">Error: {error}</div>;
  }

  // Log products to verify they're being set correctly

  return (
    <div className="bg-white p-4 min-h-[400px]">
      <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
        <h3 className="text-xl font-semibold text-slate-700 mb-4">
          Supplier Products
        </h3>

        {/* DynamicTable with built-in pagination */}
        <DynamicTable 
          columns={columns} 
          data={products}
          showPagination={true}
          showPageInfo={true}
          initialPageSize={10}
          pageSizeOptions={[10, 20, 30, 50]}
          searchable={true}
          searchPlaceholder="Search products..."
          emptyMessage="No products available for this supplier"
        />
      </div>
    </div>
  );
};

export default SupplierProduct;